import React from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableOpacity } from 'react-native';
import { InferType } from 'yup';

import Avatar from '@components/Avatar/Avatar';
import { ProfileEditModal } from '@components/Modals/ProfileEditModal.web';
import { CompleteProfilePhoneBanner } from '@components/Profile/CompleteProfilePhoneBanner';
import { Box, ShadowBox, Text } from '@components/Restyle';
import { Tag } from '@components/shared/Tags/Tag';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import useMe from '@hooks/useMe';
import saveProfileSchema from '@schemas/saveProfileSchema';
import { isMobileBrowser } from '@utils/mobileDeepLink';

export type SaveProfileFormValues = InferType<typeof saveProfileSchema>;

const EditProfile: React.FC = () => {
  const { t } = useTranslation();
  const { me } = useMe();
  const { setIsEditProfileOpen } = useWebDrawer();
  const notHasPhoneNumber = !me?.phoneNumber?.trim();
  const isMobile = isMobileBrowser();
  const openEditProfile = () => {
    setIsEditProfileOpen(true);
  };

  return (
    <>
      <ShadowBox width={isMobile ? undefined : 560}>
        <Box borderColor='grey02' borderWidth={1} flex={1} padding='m'>
          {notHasPhoneNumber && <CompleteProfilePhoneBanner />}
          <Box
            flexDirection='row'
            alignItems='center'
            justifyContent='space-between'>
            <Box flexDirection='row' alignItems='center'>
              <Box marginBottom='m' marginTop='m' marginEnd='s'>
                <Avatar
                  avatar={me?.avatar}
                  width={48}
                  height={48}
                  label={me?.name}
                />
              </Box>
              <Text color='textPrimary' variant='labelLarge'>
                {me?.name}
              </Text>
            </Box>
            {!isMobile && <ProfileEditModal popSize='big' />}
          </Box>

          <Box
            flexDirection='row'
            alignItems='center'
            marginTop='m'
            justifyContent='space-between'>
            <Text color='textPrimary' variant='labelEmphasized'>
              {t('models:saveProfile.location')}
            </Text>
            <Text color='textPrimary' variant='body'>
              {me?.address || '-'}
            </Text>
          </Box>

          <Box
            flexDirection='row'
            alignItems='center'
            marginTop='m'
            justifyContent='space-between'>
            <Text color='textPrimary' variant='labelEmphasized'>
              {t('models:saveProfile.phoneNumberText')}
            </Text>
            {!notHasPhoneNumber ? (
              <Text color='textPrimary' variant='body'>
                {t('format:phoneNumber', { val: me?.phoneNumber })}
              </Text>
            ) : (
              <TouchableOpacity
                accessibilityLabel={t(
                  'models:profile.myAccount.addPhoneNumber'
                )}
                onPress={openEditProfile}>
                <Box
                  borderColor='black'
                  borderWidth={1}
                  height={32}
                  width={161}
                  alignItems='center'
                  justifyContent='center'
                  borderRadius='xs'>
                  <Text color='black' variant='webSmall'>
                    {t('models:profile.myAccount.addPhoneNumber')}
                  </Text>
                </Box>
              </TouchableOpacity>
            )}
          </Box>

          <Box
            flexDirection='row'
            marginTop='m'
            alignItems='center'
            justifyContent='space-between'>
            <Text color='textPrimary' variant='labelEmphasized'>
              {t('models:saveProfile.emailText')}
            </Text>
            <Text color='textPrimary' variant='body'>
              {me?.email || '-'}
            </Text>
          </Box>

          <Box marginTop='m' flexDirection='row'>
            <Text
              marginRight='xs'
              variant='labelEmphasized'
              color='textPrimary'>
              {t('models:saveProfile.skills')}
            </Text>
          </Box>

          {(me?.skills?.length || 0) > 0 && (
            <Box mt='xs' flexDirection='row' flexWrap='wrap' mb='s'>
              {me?.skills?.map((skill) => (
                <Tag
                  key={skill.id}
                  label={skill.name}
                  labelVariant='labelSmall'
                  variant='status'
                  marginRight='xxs'
                  padding='xxs'
                  borderRadius='xxs'
                  marginBottom='xs'
                />
              ))}
            </Box>
          )}
        </Box>
      </ShadowBox>
    </>
  );
};
export default EditProfile;
